export type SortConfig = {
  key: string | null
  direction: 'ascending' | 'descending' | null
}

export type DataRow = {
  id: number
  type: number
  description: string
  startedDateTime: string
  reportedDateTime: string
  completedDateTime: string
  createdBy: string
  status: number
  assignedTo: string
  remarks: string
}

// sorting
export const handleSort = (
  key: string,
  sortConfig: SortConfig,
  setSortConfig: (val: SortConfig) => void
) => {
  let newDirection: 'ascending' | 'descending' = 'ascending'
  if (sortConfig.key === key && sortConfig.direction === 'ascending') {
    newDirection = 'descending'
  }
  setSortConfig({ key, direction: newDirection })
}

// format digit value
export const formatDigitValue = (value: number | null | undefined, dataType: string) => {
  const userLocale = navigator.language || 'en-US'
  if (value === null || value === undefined) {
    return '-'
  }
  if (dataType === 'WaterQuality') {
    return `${value.toLocaleString(userLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `
  } else if (dataType === 'PlanktonSurvey' || dataType === 'SedimentQuality') {
    return `${value.toLocaleString(userLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
  } else {
    return `${value.toLocaleString(userLocale, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
  }
}

// format the date in query window in map view and document list view
export const formatDate = (dateString: string, withTime: boolean = true): string => {
  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = withTime
    ? {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }
    : { year: 'numeric', month: '2-digit', day: '2-digit' }

  return date.toLocaleString('en-GB', options).replace(/,/g, '')
}

// format file size in document list view
export const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// calculate centroid multipolygon
export const calculateCentroid = (item: any): [number, number] => {
  let sumLng = 0,
    sumLat = 0,
    numPoints = 0
  const processCoordinates = (coordinates: any[]) => {
    coordinates.forEach((coord: any) => {
      if (Array.isArray(coord[0])) {
        processCoordinates(coord)
      } else {
        sumLng += coord[0]
        sumLat += coord[1]
        numPoints += 1
      }
    })
  }
  processCoordinates(item.geometry.coordinates)
  if (numPoints === 0) return [0, 0]
  return [sumLng / numPoints, sumLat / numPoints]
}

// footer
export const getYear = () => {
  const currentYear = new Date().getFullYear()
  return currentYear
}

// Function to sanitize filenames
export const sanitizeFilename = (filename: string) => {
  return filename.replace(/[^a-zA-Z0-9_.-]/g, '_')
}

// Function to create a safe download link
export const createDownloadLink = (downloadUrl: string, filename: string) => {
  try {
    const urlObject = new URL(downloadUrl)
    const allowedProtocols = ['http:', 'https:', 'blob:']

    if (!allowedProtocols.includes(urlObject.protocol)) {
      throw new Error('Invalid URL protocol')
    }

    const link = document.createElement('a')
    link.href = encodeURI(downloadUrl)
    link.setAttribute('download', filename)
    link.style.display = 'none'

    const fragment = document.createDocumentFragment()
    fragment.appendChild(link)
    document.body.appendChild(fragment)

    link.click()

    document.body.removeChild(link)
  } catch (error) {
    console.error('Invalid download URL or protocol:', error)
  }
}

export enum ResponseType {
  json = 'json',
  blob = 'blob',
  text = 'text',
  arrayBuffer = 'arraybuffer',
  formData = 'formdata',
}

export const apiResponse = async <T = any>(
  response: Response,
  responseType: ResponseType = ResponseType.json
): Promise<T> => {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`

    const responseClone = response.clone()

    try {
      const responseText = await responseClone.text()

      if (responseText.trim()) {
        try {
          const errorData = JSON.parse(responseText)

          if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            errorMessage = errorData.errors[0].reason || errorData.errors[0].message || errorMessage
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.title) {
            errorMessage = errorData.title
          } else if (errorData.error) {
            errorMessage = errorData.error
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.description) {
            errorMessage = errorData.description
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          } else {
            errorMessage = responseText
          }
        } catch (jsonError) {
          errorMessage = responseText
        }
      } else {
        errorMessage = `HTTP ${response.status} - ${response.statusText || 'Unknown Error'}`
      }
    } catch (fetchError) {
      errorMessage = `HTTP ${response.status} - ${response.statusText || 'Unknown Error'}`
    }

    throw new Error(errorMessage)
  }

  try {
    switch (responseType) {
      case ResponseType.blob:
        return (await response.blob()) as T
      case ResponseType.text:
        return (await response.text()) as T
      case ResponseType.arrayBuffer:
        return (await response.arrayBuffer()) as T
      case ResponseType.formData:
        return (await response.formData()) as T
      case ResponseType.json:
        if (response.status === 204 || response.headers.get('Content-Length') === '0') {
          return {} as T
        }
        const text = await response.text()
        return text ? JSON.parse(text) : ({} as T)
      default:
        return response as unknown as T
    }
  } catch (error) {
    throw new Error(
      `Failed to parse response as ${responseType}: ${
        error instanceof Error ? error.message : String(error)
      }`
    )
  }
}

// variant type: success, error, warning, info, or default
export enum VariantType {
  success = 'success',
  warning = 'warning',
  error = 'error',
  info = 'info',
  default = 'default',
}

// show notification
export const showSnackbar = (
  message: string,
  variant: string,
  setVariant: (val: string) => void,
  setSnackbarOpen: (val: boolean) => void,
  setSnackbarMessage: (val: string) => void
) => {
  setVariant(variant)
  setSnackbarMessage(message)
  setSnackbarOpen(true)
}
